/* ==========================================================================
   STYLES IN MOTION — theme.js (shared core, ES module, deferred)
   Native web components + fetch. No dependencies.
   Exposes window.SIM for section-level modules.
   ========================================================================== */
const SIM = (window.SIM = window.SIM || {});
const cfg = SIM.config || {};
const routes = cfg.routes || {};
const strings = cfg.strings || {};

/* ---------- Utilities ---------- */
const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
SIM.$ = $;
SIM.$$ = $$;

const motionOK = () => document.documentElement.classList.contains('motion-ok');
SIM.motionOK = motionOK;

SIM.emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));

SIM.fetchSections = async (url, sectionIds) => {
  const u = new URL(url, location.origin);
  u.searchParams.set('sections', sectionIds.join(','));
  const res = await fetch(u, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(res.status);
  return res.json();
};

SIM.fetchSectionHTML = async (url, sectionId) => {
  const u = new URL(url, location.origin);
  u.searchParams.set('section_id', sectionId);
  const res = await fetch(u);
  if (!res.ok) throw new Error(res.status);
  return res.text();
};

SIM.parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

/** DOMParser-inserted markup never executes scripts: load any external module/script once. */
SIM.loadScripts = (doc) => {
  $$('script[src]', doc).forEach((s) => {
    const src = s.getAttribute('src');
    if ($$('script[src]').some((e) => e.getAttribute('src') === src)) return;
    const el = document.createElement('script');
    el.src = src;
    if (s.type) el.type = s.type;
    document.head.append(el);
  });
  $$('link[rel="stylesheet"][href]', doc).forEach((l) => {
    const href = l.getAttribute('href');
    if ($$('link[rel="stylesheet"]').some((e) => e.getAttribute('href') === href)) return;
    document.head.append(l.cloneNode());
  });
};

/** Replace innerHTML of each [id] in target doc from a fresh section render */
SIM.swapFrom = (html, selectors) => {
  const doc = SIM.parseHTML(html);
  selectors.forEach((sel) => {
    const next = $(sel, doc);
    const cur = $(sel);
    if (next && cur) cur.innerHTML = next.innerHTML;
  });
  return doc;
};

let toastTimer;
SIM.toast = (message) => {
  let el = $('#SimToast');
  if (!el) {
    el = document.createElement('div');
    el.id = 'SimToast';
    el.className = 'toast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    document.body.append(el);
  }
  el.textContent = message;
  requestAnimationFrame(() => el.classList.add('is-visible'));
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove('is-visible'), 3200);
};

/* ---------- Local store (privacy-safe: product handles only) ---------- */
const store = {
  get(key) {
    try { return JSON.parse(localStorage.getItem(`sim:${key}`)) || []; } catch { return []; }
  },
  set(key, value) {
    try { localStorage.setItem(`sim:${key}`, JSON.stringify(value)); } catch { /* private mode */ }
  },
};
SIM.store = store;

/* ---------- Dialogs (native <dialog>) ---------- */
const openers = new WeakMap();
SIM.openDialog = (dialog, opener) => {
  if (!dialog || dialog.open) return;
  openers.set(dialog, opener || document.activeElement);
  dialog.showModal();
  document.documentElement.classList.add('has-dialog');
  dialog.dispatchEvent(new CustomEvent('dialog:open'));
};
SIM.closeDialog = (dialog) => {
  if (!dialog || !dialog.open) return;
  dialog.close();
};

document.addEventListener('click', (e) => {
  const open = e.target.closest('[data-open-dialog]');
  if (open) {
    const dlg = document.getElementById(open.dataset.openDialog);
    if (dlg) { e.preventDefault(); SIM.openDialog(dlg, open); }
    return;
  }
  const close = e.target.closest('[data-close-dialog]');
  if (close) { SIM.closeDialog(close.closest('dialog')); return; }
  // Backdrop click closes
  if (e.target instanceof HTMLDialogElement && e.target.open) {
    const r = e.target.getBoundingClientRect();
    const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
    if (!inside) SIM.closeDialog(e.target);
  }
});
document.addEventListener('close', (e) => {
  if (!(e.target instanceof HTMLDialogElement)) return;
  if (!$$('dialog[open]').length) document.documentElement.classList.remove('has-dialog');
  const opener = openers.get(e.target);
  if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
  e.target.dispatchEvent(new CustomEvent('dialog:close'));
}, true);

/* ---------- Reveal on scroll (Signature 03) ---------- */
function splitWords(el) {
  if (el.dataset.split) return;
  el.dataset.split = '1';
  const walk = (node, i = { n: 0 }) => {
    [...node.childNodes].forEach((child) => {
      if (child.nodeType === 3) {
        const frag = document.createDocumentFragment();
        child.textContent.split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) { frag.append(part); return; }
          const w = document.createElement('span');
          w.className = 'word';
          const inner = document.createElement('span');
          inner.style.setProperty('--w', i.n++);
          inner.textContent = part;
          w.append(inner);
          frag.append(w);
        });
        child.replaceWith(frag);
      } else if (child.nodeType === 1 && child.tagName !== 'BR') {
        walk(child, i);
      }
    });
  };
  walk(el);
}

let revealObserver;
const revealTargets = new Map();
SIM.initReveal = (root = document) => {
  const els = $$('[data-reveal]:not(.is-revealed)', root);
  if (!motionOK()) { els.forEach((el) => el.classList.add('is-revealed')); return; }
  revealObserver ||= new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const targets = revealTargets.get(entry.target) || [entry.target];
      targets.forEach((t) => t.classList.add('is-revealed'));
      revealTargets.delete(entry.target);
      revealObserver.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.01 });
  els.forEach((el) => {
    if (el.dataset.reveal === 'words') splitWords(el);
    // Image reveals start fully clipped (clip-path inset 100%), which Chrome's IntersectionObserver
    // treats as never visible — so watch the unclipped parent instead.
    const watch = el.dataset.reveal === 'image' && el.parentElement ? el.parentElement : el;
    if (watch !== el) revealTargets.set(watch, [...(revealTargets.get(watch) || []), el]);
    revealObserver.observe(watch);
  });
};

/* ---------- Header ---------- */
class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.mode = this.dataset.sticky || 'always'; // none | always | scroll-up
    this.transparent = this.hasAttribute('data-transparent');
    this.fitNav();
    document.fonts?.ready.then(() => this.fitNav());
    let raf;
    const refit = () => { clearTimeout(raf); raf = setTimeout(() => this.fitNav(), 60); };
    window.addEventListener('resize', refit, { passive: true });
    if (this.mode === 'none') return;
    this.last = 0;
    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
    new ResizeObserver(() => this.setHeight()).observe(this);
  }
  /* Desktop masthead is two rows by default (CSS). Go single-row only if the menu fits beside a centred logo. */
  fitNav() {
    const root = document.documentElement;
    const header = $('.header', this);
    const nav = $('.header__nav', this);
    const menu = $('.header__menu', this);
    const logo = $('.header__logo-wrap', this);
    if (!header || !nav || !menu || !logo) return;
    if (header.classList.contains('header--logo-left')) { root.classList.add('header-inline'); return; }
    if (!nav.offsetParent) return; // mobile: nav hidden
    const cs = getComputedStyle(header);
    const content = header.clientWidth - parseFloat(cs.paddingInlineStart) - parseFloat(cs.paddingInlineEnd);
    const column = (content - logo.offsetWidth) / 2 - 32;
    const items = menu.children;
    if (!items.length) return;
    const first = items[0].getBoundingClientRect();
    const last = items[items.length - 1].getBoundingClientRect();
    const needed = Math.abs(last.right - first.left) || menu.scrollWidth; // intrinsic width of the items, not the row
    root.classList.toggle('header-inline', needed <= column);
    // Compact (scrolled) row: the icons slide into the menu row → keep the menu clear of them.
    // Language switch is hidden there, so measure the icons without it. Centred when it fits, else pushed left.
    const icons = $('.header__icons', this);
    if (icons) {
      const lang = $('.lang-switch', icons);
      const end = icons.offsetWidth - (lang ? lang.offsetWidth : 0) + 24;
      const centred = needed + 2 * end <= content;
      root.style.setProperty('--compact-pad-end', `${end}px`);
      root.style.setProperty('--compact-pad-start', centred ? `${end}px` : '0px');
    }
  }
  disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
  /* Visible header height (compact desktop mode hides the logo row with a transform) */
  setHeight() {
    let h = this.offsetHeight;
    if (this.isCompact()) h -= parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-bar')) || 0;
    document.documentElement.style.setProperty('--header-live-height', `${h}px`);
  }
  isCompact() {
    return this.hasAttribute('data-compact') && this.classList.contains('is-scrolled')
      && !document.documentElement.classList.contains('header-inline') && matchMedia('(min-width: 990px)').matches;
  }
  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      const y = window.scrollY;
      const threshold = this.transparent ? 40 : this.offsetHeight;
      const was = this.classList.contains('is-scrolled');
      this.classList.toggle('is-scrolled', y > threshold);
      if (was !== this.classList.contains('is-scrolled')) this.setHeight();
      if (this.mode === 'scroll-up') {
        const hide = y > this.last && y > this.offsetHeight * 2 && !document.documentElement.classList.contains('has-dialog');
        this.classList.toggle('is-hidden', hide);
      }
      this.last = y;
      this.ticking = false;
    });
  }
}
customElements.define('sticky-header', StickyHeader);

/* Desktop mega menu: <details> based, one open at a time, close on outside click / Esc */
class MegaMenu extends HTMLElement {
  connectedCallback() {
    this.items = $$('details', this);
    this.items.forEach((d) => {
      d.addEventListener('toggle', () => {
        if (d.open) this.items.forEach((o) => o !== d && (o.open = false));
        this.classList.toggle('has-open', this.items.some((o) => o.open));
      });
      if (matchMedia('(hover: hover)').matches) {
        let t;
        d.addEventListener('mouseenter', () => { clearTimeout(t); d.open = true; });
        d.addEventListener('mouseleave', () => { t = setTimeout(() => (d.open = false), 160); });
      }
    });
    document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.closeAll(); });
    this.addEventListener('keyup', (e) => {
      if (e.key !== 'Escape') return;
      const open = this.items.find((d) => d.open);
      if (open) { open.open = false; $('summary', open).focus(); }
    });
    this.addEventListener('focusout', () => {
      setTimeout(() => { if (!this.contains(document.activeElement)) this.closeAll(); });
    });
  }
  closeAll() { this.items.forEach((d) => (d.open = false)); }
}
customElements.define('mega-menu', MegaMenu);

/* ---------- Cart ---------- */
SIM.cart = {
  sections() {
    return $$('[data-cart-section]').map((el) => el.dataset.cartSection);
  },
  async add(formData) {
    const sections = this.sections();
    if (sections.length) formData.append('sections', sections.join(','));
    formData.append('sections_url', location.pathname);
    const res = await fetch(`${routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || strings.cartError);
    this.render(data.sections);
    SIM.emit('cart:updated', { source: 'add', item: data });
    return data;
  },
  async change(line, quantity) {
    const sections = this.sections();
    const res = await fetch(`${routes.cart_change_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity, sections, sections_url: location.pathname }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || strings.cartError);
    this.render(data.sections);
    SIM.emit('cart:updated', { source: 'change', cart: data });
    return data;
  },
  render(sections) {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      $$(`[data-cart-section="${id}"]`).forEach((target) => {
        const doc = SIM.parseHTML(html);
        const fresh = $(`[data-cart-section="${id}"]`, doc);
        if (fresh) target.innerHTML = fresh.innerHTML;
      });
    });
    SIM.initReveal();
  },
  open(opener) {
    const drawer = $('#CartDrawer');
    if (drawer && cfg.cartType === 'drawer') SIM.openDialog(drawer, opener);
    else location.href = routes.cart_url;
  },
};

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = $('form', this);
    if (!this.form || cfg.cartType !== 'drawer') return;
    this.form.addEventListener('submit', (e) => this.onSubmit(e));
  }
  async onSubmit(e) {
    e.preventDefault();
    const btn = e.submitter || $('[type="submit"]', this.form);
    if (!btn || btn.getAttribute('aria-busy') === 'true') return;
    const error = $('[data-form-error]', this);
    if (error) error.hidden = true;
    btn.setAttribute('aria-busy', 'true');
    try {
      await SIM.cart.add(new FormData(this.form));
      const quick = this.closest('dialog');
      if (quick && quick.id !== 'CartDrawer') SIM.closeDialog(quick);
      SIM.cart.open(btn);
    } catch (err) {
      if (error) { error.textContent = err.message; error.hidden = false; }
      else SIM.toast(err.message);
    } finally {
      btn.removeAttribute('aria-busy');
    }
  }
}
customElements.define('product-form', ProductForm);

/* Quick add for multi-variant products: loads a compact product form into a sheet */
class QuickAddButton extends HTMLElement {
  connectedCallback() {
    $('button', this)?.addEventListener('click', (e) => this.open(e.currentTarget));
  }
  async open(btn) {
    const dialog = $('#QuickAdd');
    if (!dialog) { location.href = this.dataset.url; return; }
    btn.setAttribute('aria-busy', 'true');
    try {
      const html = await SIM.fetchSectionHTML(this.dataset.url, 'quick-add');
      const doc = SIM.parseHTML(html);
      $('[data-quick-add-body]', dialog).replaceChildren(...doc.body.firstElementChild.childNodes);
      SIM.loadScripts(doc);
      SIM.openDialog(dialog, btn);
    } catch {
      location.href = this.dataset.url;
    } finally {
      btn.removeAttribute('aria-busy');
    }
  }
}
customElements.define('quick-add-button', QuickAddButton);

/* Cart line quantity / remove (used by drawer and cart page) */
class CartItems extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-qty-change], [data-remove-line]');
      if (!btn) return;
      const line = Number(btn.closest('[data-line]').dataset.line);
      const input = $(`[data-line="${line}"] .qty__input`, this);
      let qty = btn.hasAttribute('data-remove-line') ? 0 : Number(input.value) + Number(btn.dataset.qtyChange);
      this.update(line, Math.max(0, qty));
    });
    this.addEventListener('change', (e) => {
      if (!e.target.matches('.qty__input')) return;
      const line = Number(e.target.closest('[data-line]').dataset.line);
      this.update(line, Math.max(0, Number(e.target.value)));
    });
  }
  async update(line, qty) {
    this.setAttribute('aria-busy', 'true');
    try { await SIM.cart.change(line, qty); }
    catch (err) { SIM.toast(err.message); }
    finally { this.removeAttribute('aria-busy'); }
  }
}
customElements.define('cart-items', CartItems);

/* Open cart drawer from bag icon */
document.addEventListener('click', (e) => {
  const link = e.target.closest('[data-cart-toggle]');
  if (!link || cfg.cartType !== 'drawer') return;
  e.preventDefault();
  SIM.cart.open(link);
});

/* ---------- Wishlist — "Your Motion" (device only, handles only) ---------- */
SIM.wishlist = {
  all: () => store.get('wishlist'),
  has: (h) => store.get('wishlist').includes(h),
  toggle(handle) {
    let list = store.get('wishlist');
    const added = !list.includes(handle);
    list = added ? [handle, ...list].slice(0, 60) : list.filter((h) => h !== handle);
    store.set('wishlist', list);
    SIM.emit('wishlist:updated', { handle, added, count: list.length });
    return added;
  },
};

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.btn = $('button', this);
    this.sync = this.sync.bind(this);
    this.sync();
    this.btn.addEventListener('click', (e) => {
      e.preventDefault();
      const added = SIM.wishlist.toggle(this.dataset.handle);
      SIM.toast(added ? strings.wishlistAdded : strings.wishlistRemoved);
    });
    document.addEventListener('wishlist:updated', this.sync);
  }
  disconnectedCallback() { document.removeEventListener('wishlist:updated', this.sync); }
  sync() {
    const on = SIM.wishlist.has(this.dataset.handle);
    this.btn.setAttribute('aria-pressed', String(on));
    this.btn.setAttribute('aria-label', on ? this.btn.dataset.labelRemove : this.btn.dataset.labelAdd);
  }
}
customElements.define('wishlist-button', WishlistButton);

const syncWishCount = () => {
  const n = SIM.wishlist.all().length;
  $$('[data-wishlist-count]').forEach((el) => { el.textContent = n; el.hidden = n === 0; });
};
document.addEventListener('wishlist:updated', syncWishCount);

/* ---------- Recently viewed — "Back in Motion" ---------- */
SIM.recent = {
  all: () => store.get('recent'),
  add(handle) {
    const list = [handle, ...store.get('recent').filter((h) => h !== handle)].slice(0, 12);
    store.set('recent', list);
  },
};

/* Renders a stored list of handles as product cards via Section Rendering */
class StoredProducts extends HTMLElement {
  async connectedCallback() {
    const source = this.dataset.source; // wishlist | recent
    const exclude = this.dataset.exclude;
    const limit = Number(this.dataset.limit || 8);
    const handles = (source === 'wishlist' ? SIM.wishlist.all() : SIM.recent.all())
      .filter((h) => h !== exclude)
      .slice(0, limit);
    const wrapper = this.closest('[data-stored-wrapper]') || this;
    if (!handles.length) { wrapper.hidden = true; this.dispatchEvent(new CustomEvent('stored:empty', { bubbles: true })); return; }
    const list = $('[data-stored-list]', this);
    const cards = await Promise.all(handles.map(async (h) => {
      try {
        const html = await SIM.fetchSectionHTML(`${routes.root_url.replace(/\/$/, '')}/products/${encodeURIComponent(h)}`, 'product-card-render');
        const card = $('[data-product-card]', SIM.parseHTML(html));
        return card;
      } catch { return null; }
    }));
    const valid = cards.filter(Boolean);
    if (!valid.length) { wrapper.hidden = true; return; }
    list.replaceChildren(...valid.map((card) => {
      const li = document.createElement('li');
      li.className = this.dataset.itemClass || '';
      li.append(document.importNode(card, true));
      return li;
    }));
    wrapper.hidden = false;
    SIM.initReveal(this);
  }
}
customElements.define('stored-products', StoredProducts);

/* ---------- Product rail controls & progress ---------- */
class ProductRail extends HTMLElement {
  connectedCallback() {
    this.track = $('[data-rail-track]', this);
    if (!this.track) return;
    // Arrows usually live in the section header, outside <product-rail>: look in the section too.
    const scope = (sel) => $(sel, this) || $(sel, this.closest('.shopify-section, section') || this);
    this.prev = scope('[data-rail-prev]');
    this.next = scope('[data-rail-next]');
    this.bar = scope('[data-rail-progress]');
    const rtl = document.dir === 'rtl' ? -1 : 1;
    const step = () => this.track.clientWidth * 0.8 * rtl;
    const behavior = () => (motionOK() ? 'smooth' : 'auto');
    // Arrows are always clickable (client 2026-10-18): at the end "next" returns to the start, at the start
    // "prev" jumps to the end. They are hidden only when the rail has nothing to scroll.
    const max = () => this.track.scrollWidth - this.track.clientWidth;
    const at = () => Math.abs(this.track.scrollLeft);
    this.prev?.addEventListener('click', () => {
      if (at() <= 2) this.track.scrollTo({ left: max() * rtl, behavior: behavior() });
      else this.track.scrollBy({ left: -step(), behavior: behavior() });
    });
    this.next?.addEventListener('click', () => {
      if (at() >= max() - 2) this.track.scrollTo({ left: 0, behavior: behavior() });
      else this.track.scrollBy({ left: step(), behavior: behavior() });
    });
    this.update = this.update.bind(this);
    this.track.addEventListener('scroll', this.update, { passive: true });
    new ResizeObserver(this.update).observe(this.track);
    // late images / reveal animations change the scroll width without resizing the track
    this.track.addEventListener('load', () => requestAnimationFrame(this.update), { capture: true });
    window.addEventListener('load', this.update, { once: true });
    // Cards rendered later (recently viewed, wishlist) change the scroll width, not the box size.
    new MutationObserver(() => requestAnimationFrame(this.update)).observe(this.track, { childList: true });
    this.update();
  }
  update() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const x = Math.abs(this.track.scrollLeft);
    const scrollable = max > 2;
    [this.prev, this.next].forEach((b) => { if (b) { b.disabled = !scrollable; b.hidden = !scrollable; } });
    this.prev?.classList.toggle('is-at-edge', x <= 2);
    this.next?.classList.toggle('is-at-edge', x >= max - 2);
    if (this.bar) {
      const visible = this.track.clientWidth / this.track.scrollWidth;
      const p = max > 0 ? visible + (1 - visible) * (x / max) : 1;
      this.bar.style.setProperty('--progress', p.toFixed(3));
      this.bar.parentElement.hidden = max <= 2;
    }
  }
}
customElements.define('product-rail', ProductRail);

/* ---------- Card hover image: injected on first hover, mouse devices only ---------- */
if (matchMedia('(hover: hover) and (pointer: fine)').matches) {
  document.addEventListener('pointerover', (e) => {
    const card = e.target.closest?.('.card--hover');
    if (!card || card.dataset.hoverReady) return;
    card.dataset.hoverReady = '1';
    const tpl = $('template[data-hover-img]', card);
    if (tpl) tpl.replaceWith(tpl.content.cloneNode(true));
  }, { passive: true });
}

/* ---------- Marquee pause control (WCAG 2.2.2) ---------- */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-marquee-toggle]');
  if (!btn) return;
  const m = btn.closest('[data-marquee]');
  const paused = m.classList.toggle('is-paused');
  btn.setAttribute('aria-pressed', String(paused));
});

/* ---------- Boot ---------- */
const boot = () => {
  SIM.initReveal();
  syncWishCount();
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();

/* Theme editor: re-init when sections reload */
document.addEventListener('shopify:section:load', (e) => SIM.initReveal(e.target));

/* ---------- Chat assistant photo (snippets/chat-assistant.liquid) ----------
   Adds the agent's photo + name to the Shopify Inbox chat header.
   Widget structure: <shopify-chat> > #shadow > <shopify-agent-runtime> > #shadow > .panel__header */
class ChatAssistant extends HTMLElement {
  connectedCallback() {
    let tries = 0;
    const find = () => {
      const root = document.querySelector('shopify-chat')?.shadowRoot?.querySelector('shopify-agent-runtime')?.shadowRoot;
      if (root?.querySelector('.panel__header')) return this.attach(root);
      if (++tries < 60) setTimeout(find, 500); // the widget loads late; stop after ~30s
    };
    find();
  }
  attach(root) {
    if (!root.getElementById('sim-agent-css')) {
      const css = document.createElement('style');
      css.id = 'sim-agent-css';
      css.textContent = `
        .sim-agent { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); display: flex; align-items: center; gap: 8px; padding: 3px 14px 3px 3px; border-radius: 999px; background: rgba(255, 244, 232, .94); box-shadow: 0 2px 10px rgba(33, 27, 29, .08); pointer-events: none; white-space: nowrap; }
        /* pill: chat messages scroll under the (transparent) header */
        .sim-agent img { width: 36px; height: 36px; border-radius: 50%; object-fit: cover; box-shadow: 0 0 0 2px #FFF4E8, 0 0 0 3px #6E2639; }
        .sim-agent span { display: grid; line-height: 1.15; text-align: start; }
        .sim-agent b { font-family: 'Playfair Display', Georgia, serif; font-weight: 500; font-size: 15px; color: #6E2639; }
        .sim-agent small { font-size: 10px; letter-spacing: .12em; text-transform: uppercase; color: #6b5a5f; }
        @media (max-width: 420px) { .sim-agent small { display: none; } .sim-agent { padding-inline-end: 12px; } }`;
      root.appendChild(css);
    }
    const place = () => {
      const header = root.querySelector('.panel__header');
      if (!header || header.querySelector('.sim-agent')) return;
      const el = document.createElement('div');
      el.className = 'sim-agent';
      el.setAttribute('aria-hidden', 'true'); // decorative: the chat already has its own accessible title
      const img = document.createElement('img');
      img.src = this.dataset.avatar; img.alt = ''; img.width = 36; img.height = 36;
      const text = document.createElement('span');
      const name = document.createElement('b'); name.textContent = this.dataset.name;
      const role = document.createElement('small'); role.textContent = this.dataset.role;
      text.append(name, role);
      el.append(img, text);
      header.appendChild(el);
    };
    place();
    // The widget re-renders its header (new chat, sign in): put the photo back when it disappears.
    new MutationObserver(place).observe(root, { childList: true, subtree: true });
  }
}
customElements.define('chat-assistant', ChatAssistant);
